import React, { useState } from 'react';
import { Container, Typography, Box, Button, Stack, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import SceneInput from './components/SceneInput';
import ActionVerbsDisplay from './components/ActionVerbsDisplay';
import ReplayIcon from '@mui/icons-material/Replay';
import { ENDPOINTS } from './config/api';

// Define styles for PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
  },
  title: {
    fontSize: 24,
    marginBottom: 30,
    textAlign: 'center',
  },
  analysisTitle: {
    fontSize: 18,
    marginTop: 20,
    marginBottom: 15,
    textDecoration: 'underline',
  },
  character: {
    marginBottom: 15,
    breakInside: 'avoid',
  },
  characterName: {
    fontSize: 14,
    marginBottom: 8,
    fontWeight: 'bold',
  },
  verbsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  verb: {
    padding: '3px 6px',
    borderRadius: 4,
    border: '1px solid black',
    marginRight: 6,
    marginBottom: 6,
    fontSize: 11,
  },
});

// PDF Document component that includes all analyses
const AllActionVerbsPDF = ({ actionVerbsList }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Text style={styles.title}>Action Verbs Analysis</Text>
      {actionVerbsList.map((data, analysisIndex) => (
        <View key={analysisIndex} wrap={false}>
          <Text style={styles.analysisTitle}>
            Analysis {analysisIndex + 1}
          </Text>
          {data.characters.map((character, index) => (
            <View key={index} style={styles.character}>
              <Text style={styles.characterName}>{character.name}</Text>
              <View style={styles.verbsContainer}>
                {character.actionVerbs.map((verb, verbIndex) => (
                  <Text key={verbIndex} style={styles.verb}>
                    {verb}
                  </Text>
                ))}
              </View>
            </View>
          ))}
        </View>
      ))}
    </Page>
  </Document>
);

const generateMarkdownContent = (actionVerbsList) => {
  let markdown = '# Action Verbs Analysis\n\n';
  
  actionVerbsList.forEach((data, analysisIndex) => {
    markdown += `## Analysis ${analysisIndex + 1}\n\n`;
    
    data.characters.forEach(character => {
      markdown += `### ${character.name}\n\n`;
      markdown += character.actionVerbs.map(verb => `- ${verb}`).join('\n');
      markdown += '\n\n';
    });
  });
  
  return markdown;
};

const downloadMarkdown = (content, filename) => {
  const blob = new Blob([content], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const App = () => {
  const [actionVerbsList, setActionVerbsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastInputs, setLastInputs] = useState(null);
  const [language, setLanguage] = useState('EN');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const generateActionVerbs = async (scene, summary, objectives, isRerun = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(ENDPOINTS.analyze, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          scene,
          summary,
          objectives,
          language,
          privacyAccepted: true  // We know it's true because the button is disabled otherwise
        }, null, 2)  // Pretty print JSON to avoid minification issues
      });

      // Log the raw response for debugging
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      // First check if the response is ok
      if (!response.ok) {
        const errorText = await response.text();  // Get raw response text
        console.error('Error response text:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        throw new Error(errorData.details || errorData.error || 'Failed to analyze scene');
      }

      // Get the raw response text first
      const responseText = await response.text();
      console.log('Raw response text:', responseText);

      if (!responseText) {
        throw new Error('Empty response received from server');
      }

      // Then try to parse it as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parsing error:', parseError);
        console.error('Response text that failed to parse:', responseText);
        throw new Error('Invalid response format from server');
      }
      
      if (!data) {
        throw new Error('Empty data received from server');
      }
      
      if (!data.characters || !Array.isArray(data.characters)) {
        console.error('Invalid response structure:', data);
        throw new Error('Invalid response format received from server');
      }

      if (!isRerun) {
        setActionVerbsList([data]);
      } else {
        setActionVerbsList(prevList => [...prevList, data]);
      }

      // Store inputs for potential rerun
      setLastInputs({ scene, summary, objectives });
      
    } catch (error) {
      console.error('Error details:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRerun = () => {
    if (lastInputs) {
      generateActionVerbs(lastInputs.scene, lastInputs.summary, lastInputs.objectives, true);
    }
  };

  const handleMarkdownDownload = () => {
    const markdown = generateMarkdownContent(actionVerbsList);
    downloadMarkdown(markdown, 'action-verb-markdown.md');
  };

  return (
    <Container maxWidth="md" sx={{ color: 'text.primary' }}>
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControl size="small" sx={{ 
            minWidth: 80,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.87)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.87)',
              }
            },
            '& .MuiInputLabel-root': {
              color: 'text.primary',
              '&.Mui-focused': {
                color: 'rgba(255, 255, 255, 0.87)'
              }
            },
            '& .MuiSelect-icon': {
              color: 'rgba(255, 255, 255, 0.87)'
            }
          }}>
            <InputLabel id="language-select-label">Lang</InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              value={language}
              label="Lang"
              onChange={(e) => setLanguage(e.target.value)}
              sx={{
                color: 'text.primary',
                '& .MuiSelect-select': {
                  py: 1
                }
              }}
            >
              <MenuItem value="EN">EN</MenuItem>
              <MenuItem value="DE">DE</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <SceneInput 
          onSubmit={generateActionVerbs} 
          loading={loading} 
          onPrivacyChange={setPrivacyAccepted}
        />
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            Error: {error}
          </Typography>
        )}
        {actionVerbsList.length > 0 && (
          <Box sx={{ 
            mt: 3, 
            mb: 2, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-end', sm: 'center' },
            justifyContent: 'flex-end',
            gap: 2
          }}>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={handleRerun}
              disabled={loading || !privacyAccepted}
              sx={{ 
                textTransform: 'none',
                borderColor: '#f94c57',
                color: 'text.primary',
                whiteSpace: 'nowrap',
                width: { xs: '200px', sm: 'fit-content' },
                minWidth: { sm: 'max-content' },
                px: { sm: 2 },
                '&:hover': {
                  borderColor: '#d32f2f',
                }
              }}
            >
              {loading ? 'Analyzing... (10-15 seconds)' : 'Rerun Analysis'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={handleMarkdownDownload}
              sx={{ 
                textTransform: 'none',
                borderColor: '#f94c57',
                color: 'text.primary',
                whiteSpace: 'nowrap',
                width: { xs: '200px', sm: 'fit-content' },
                minWidth: { sm: 'max-content' },
                px: { sm: 2 },
                '&:hover': {
                  borderColor: '#d32f2f',
                }
              }}
            >
              Download Markdown
            </Button>
            <PDFDownloadLink
              document={<AllActionVerbsPDF actionVerbsList={actionVerbsList} />}
              fileName="action-verb-PDF.pdf"
              style={{ 
                display: 'block',
                width: 'auto',
                textAlign: 'right'
              }}
            >
              {({ loading: pdfLoading }) => (
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  disabled={pdfLoading}
                  sx={{ 
                    textTransform: 'none',
                    bgcolor: '#f94c57',
                    width: { xs: '200px', sm: 'fit-content' },
                    minWidth: { sm: 'max-content' },
                    px: { sm: 2 },
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: '#d32f2f',
                    }
                  }}
                >
                  {pdfLoading ? 'Preparing PDF...' : 'Download PDF'}
                </Button>
              )}
            </PDFDownloadLink>
          </Box>
        )}
        {actionVerbsList.map((actionVerbs, index) => (
          <ActionVerbsDisplay 
            key={index} 
            data={actionVerbs} 
            loading={loading}
            isLastResult={index === actionVerbsList.length - 1}
          />
        ))}
      </Box>
    </Container>
  );
};

export default App;
